import { useEffect } from 'react';

// Canonical production domain — used for every canonical URL and every
// JSON-LD @id / url field. Never uses preview or localhost URLs.
export const SITE = 'https://kennyvicops.com';

const ensureTag = (selector, create) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
};

/**
 * Lightweight, dependency-free SEO helper. Injects <title>,
 * <meta name="description">, <link rel="canonical">, <meta property="og:*">,
 * and any number of JSON-LD <script type="application/ld+json"> blocks.
 *
 * Every page-level JSON-LD script is tagged with data-seo="page" and removed
 * on route change so no stale schema leaks across pages.
 */
export const SEO = ({ path = '/', title, description, image, jsonLd = [] }) => {
  useEffect(() => {
    const cleanPath = path === '/' ? '/' : path.replace(/\/+$/, '');
    const url = `${SITE}${cleanPath}`;

    if (title) document.title = title;

    if (description) {
      const meta = ensureTag('meta[name="description"]', () => {
        const m = document.createElement('meta');
        m.setAttribute('name', 'description');
        return m;
      });
      meta.setAttribute('content', description);
    }

    // canonical
    const canonical = ensureTag('link[rel="canonical"]', () => {
      const l = document.createElement('link');
      l.setAttribute('rel', 'canonical');
      return l;
    });
    canonical.setAttribute('href', url);

    // og:url + og:title + og:description + og:image
    const ogPairs = [
      ['og:url', url],
      ['og:title', title],
      ['og:description', description],
      image ? ['og:image', image.startsWith('http') ? image : `${SITE}${image}`] : null,
      ['og:type', 'website'],
      ['og:site_name', 'Kennyvicops Cyber Solutions Inc.'],
    ].filter(Boolean);
    ogPairs.forEach(([property, content]) => {
      if (!content) return;
      const tag = ensureTag(`meta[property="${property}"]`, () => {
        const m = document.createElement('meta');
        m.setAttribute('property', property);
        return m;
      });
      tag.setAttribute('content', content);
    });

    // Remove previous page-level JSON-LD (leaves the site-wide graph in index.html untouched)
    document.head
      .querySelectorAll('script[data-seo="page"]')
      .forEach((s) => s.remove());

    const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    blocks.filter(Boolean).forEach((obj) => {
      const s = document.createElement('script');
      s.setAttribute('type', 'application/ld+json');
      s.setAttribute('data-seo', 'page');
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
    });
  }, [path, title, description, image, JSON.stringify(jsonLd)]);

  return null;
};

// Helpers used by page components ------------------------------------------

export const orgRef = { '@id': `${SITE}/#organization` };
export const personRef = { '@id': `${SITE}/about#kenny-vic` };
export const websiteRef = { '@id': `${SITE}/#website` };

export const breadcrumb = (crumbs) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: crumbs.map((c, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: c.name,
    item: `${SITE}${c.path}`,
  })),
});

export default SEO;
